/**
 * AcceptInvite — landing page for /accept-invite?token=<invite token>
 *
 * The email carries only our single-use invite token. We trade it
 * (POST /v1/team/invites/redeem) for a Supabase OTP minted right now, redeem
 * that with supabase.auth.verifyOtp (works under the PKCE-only client), then
 * POST the invite token to /v1/team/accept — which binds it to the invited
 * email, so a session for any other address is refused.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { acceptInvite, redeemInvite } from '@/lib/team';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export function AcceptInvite() {
  useEffect(() => { document.title = 'Accept your invitation — Waypoint'; }, []);
  const [params] = useSearchParams();
  const token = params.get('token');
  const started = useRef(false);
  const { loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'working' | 'ok' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || started.current) return;
    if (!token) {
      setStatus('error');
      setError('Missing invite token.');
      return;
    }
    started.current = true;
    setStatus('working');
    (async () => {
      // Sign in as the invitee. If redeem/verify fails (e.g. a reload after the
      // token was spent) fall through when a session already exists; accept
      // enforces the email match and single-use either way.
      try {
        const { token_hash, type } = await redeemInvite(token);
        const { error: otpErr } = await supabase.auth.verifyOtp({ token_hash, type });
        if (otpErr) throw otpErr;
      } catch (e) {
        const { data } = await supabase.auth.getSession();
        if (!data.session) throw e;
      }
      await acceptInvite(token);
      setStatus('ok');
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    })().catch((err: Error) => {
      setStatus('error');
      setError(err.message);
    });
  }, [loading, token, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface-1 p-8 text-center">
        <div className="font-mono text-2xs uppercase tracking-widest text-primary-bright">
          ◆ Waypoint Intel
        </div>
        <h1 className="mt-2 text-2xl text-ink">Joining your team…</h1>

        {status === 'idle' && (
          <p className="mt-4 text-sm text-ink-muted">Checking your invite.</p>
        )}
        {status === 'working' && (
          <p className="mt-4 text-sm text-ink-muted">Adding you to the workspace.</p>
        )}
        {status === 'ok' && (
          <>
            <p className="mt-4 text-sm text-success">✓ You're in. Redirecting to the dashboard.</p>
            <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary-bright underline">
              Open dashboard
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="mt-4 text-sm text-danger">! {error}</p>
            <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary-bright underline">
              Continue to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
